import { useState, useEffect, useCallback, createContext, useContext, Component, lazy, Suspense, type ReactNode, type ErrorInfo } from "react";
import { Authenticated, AuthLoading, Unauthenticated, useQuery } from "convex/react";
import { useAuthActions } from "@convex-dev/auth/react";
import { Router, Route, Switch, useLocation } from "wouter";
import { useHashLocation } from "wouter/use-hash-location";
import { api } from "../../convex/_generated/api";
import { ProjectSelector, type Selection } from "./ProjectSelector";
import { StatusHeader } from "./StatusHeader";
import { ListView } from "./ListView";
import { GraphView } from "./GraphView";
import { useTaskNotifications, useBuildVersion } from "../api/hooks";
import { SubscriptionScope } from "../api/components/SubscriptionScope";
import { ProjectDashboardProvider, useRawTaskStatuses } from "../api/context/ProjectDashboardContext";
import { ApiKeysPage } from "./ApiKeys";
import { AssetViewer } from "./AssetViewer";
import { DevServerPage } from "./DevServer";
import { ProjectSettings } from "./ProjectSettings";
import { RepoHome } from "./RepoHome";
import { UserManagementPage } from "./UserManagement";
import { ClaudeSessionsPage } from "./ClaudeSessions";
import { FleetView } from "../pages/FleetView";
import { LeaderboardPage } from "../pages/Leaderboard";
import { TemplateManager } from "../components/TemplateManager";
import { TemplateLibraryPage } from "../pages/TemplateLibrary";
import type { Id } from "../../convex/_generated/dataModel";
import { safeDecodeURIComponent } from "../api/hooks/useRouteState";

// FR-D08 / NFR-003: Recharts-heavy pages lazy-loaded to keep initial bundle small
const CostAttributionPage = lazy(() =>
  import("../pages/CostAttribution").then((m) => ({ default: m.CostAttributionPage }))
);
// RegressionDetail is also Recharts-heavy; lazy-loaded here for future use when
// FleetView is refactored to consume it from this module instead of importing directly.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _LazyRegressionDetail = lazy(() =>
  import("../pages/RegressionDetail").then((m) => ({ default: m.RegressionDetail }))
);

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-20 text-zinc-600">
      Loading...
    </div>
  );
}

type Tab = "graph" | "list" | "assets" | "settings";
type RepoTab = "prds" | "inbox" | "settings";

// FR-D08: Route type replaced by wouter <Route> components with hash location hook.
// parseHash() removed — wouter handles URL matching via <Switch>/<Route>.

const REPO_RESERVED = new Set<string>(["prds", "inbox", "settings"]);
const VALID_TABS = new Set<string>(["graph", "list", "assets", "settings"]);
const VALID_REPO_TABS = new Set<string>(["prds", "inbox", "settings"]);

// --- Global error toast ---

type Toast = { id: number; message: string };
const ErrorToastContext = createContext<(msg: string) => void>(() => {});
export const useErrorToast = () => useContext(ErrorToastContext);

let _toastId = 0;
function ErrorToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showError = useCallback((message: string) => {
    const id = ++_toastId;
    setToasts((prev) => [...prev, { id, message }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 8000);
  }, []);

  return (
    <ErrorToastContext.Provider value={showError}>
      {children}
      {toasts.length > 0 && (
        <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 max-w-md">
          {toasts.map((t) => (
            <div
              key={t.id}
              className="bg-red-500/10 border border-red-500/30 text-red-300 rounded-lg px-4 py-3 text-sm shadow-lg backdrop-blur-sm flex items-start gap-2"
            >
              <span className="text-red-400 shrink-0 mt-0.5">&#x2717;</span>
              <span className="break-words">{t.message}</span>
              <button
                onClick={() => setToasts((prev) => prev.filter((x) => x.id !== t.id))}
                className="shrink-0 text-red-400/60 hover:text-red-300 ml-2"
              >
                &times;
              </button>
            </div>
          ))}
        </div>
      )}
    </ErrorToastContext.Provider>
  );
}

function SignInForm() {
  const { signIn } = useAuthActions();
  const showError = useErrorToast();
  const [loading, setLoading] = useState(false);

  const handleSignIn = async () => {
    setLoading(true);
    try {
      await signIn("google");
    } catch (err: any) {
      const msg = err?.message ?? String(err);
      showError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="flex flex-col items-center gap-6 p-8">
        <h1 className="text-xl font-semibold tracking-tight text-zinc-100">Task Tracker</h1>
        <p className="text-sm text-zinc-500">Sign in to view PRD execution status</p>
        <button
          onClick={() => void handleSignIn()}
          disabled={loading}
          className="flex items-center gap-3 bg-zinc-900 text-zinc-200 border border-zinc-700 rounded-lg px-6 py-3 hover:bg-zinc-800 hover:border-zinc-600 transition-colors text-sm font-medium disabled:opacity-50"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
          </svg>
          {loading ? "Signing in..." : "Sign in with Google"}
        </button>
        {import.meta.env.DEV && (
          <button
            onClick={() => void signIn("anonymous").catch((err: any) => showError(err?.message ?? String(err)))}
            className="text-sm text-zinc-500 hover:text-zinc-300 border border-zinc-800 rounded-lg px-4 py-2 transition-colors"
          >
            Dev: Anonymous sign in
          </button>
        )}
      </div>
    </div>
  );
}

function AccessDenied() {
  const { signOut } = useAuthActions();
  const user = useQuery(api.users.currentUser);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="flex flex-col items-center gap-4 p-8 text-center">
        <div className="w-12 h-12 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center text-red-400 text-xl">
          ✕
        </div>
        <h2 className="text-lg font-semibold text-zinc-100">Access Denied</h2>
        <p className="text-sm text-zinc-500 max-w-xs">
          {user?.email ? `${user.email} is` : "Your account is"} not authorized to use this application.
        </p>
        <button
          onClick={() => void signOut()}
          className="text-sm text-zinc-400 hover:text-zinc-200 border border-zinc-700 rounded-lg px-4 py-2 transition-colors"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

function AuthenticatedApp() {
  const user = useQuery(api.users.currentUser);

  if (user === undefined) {
    return <div className="min-h-screen flex items-center justify-center text-zinc-600">Loading...</div>;
  }

  if (!user?.isAllowed) {
    return <AccessDenied />;
  }

  return <Dashboard user={user} />;
}

function Dashboard({ user }: { user: { email?: string; name?: string } }) {
  const { signOut } = useAuthActions();
  const projects = useQuery(api.projects.list, {});
  const repositories = useQuery(api.repositories.list, {});
  const [location, navigate] = useLocation();
  const [selectedRepoId, setSelectedRepoId] = useState<Id<"repositories"> | null>(null);
  const [selection, setSelection] = useState<Selection | null>(null);
  const [tab, setTab] = useState<Tab>("graph");
  const [hideComplete, setHideComplete] = useState(false);

  const selectedProjectId = selection?.type === "project" ? selection.projectId : null;

  const unblockedTasks = useQuery(api.tasks.unblocked, selectedProjectId ? { projectId: selectedProjectId } : "skip");
  const selectedProject = projects?.find((p) => p._id === selectedProjectId);
  // T332: Notification diff-tracking — statuses fed by NotificationBridge inside ProjectDashboardProvider
  const [notifStatuses, setNotifStatuses] = useState<{ taskId: string; status: string; subject: string }[] | undefined>(undefined);
  const notifications = useTaskNotifications({ statuses: notifStatuses, projectName: selectedProject?.name });
  const { updateAvailable, localVersion, serverVersion, refresh } = useBuildVersion();
  const [notifBannerDismissed, setNotifBannerDismissed] = useState(
    () => localStorage.getItem("task-tracker-notif-banner-dismissed") === "true"
  );

  const dismissNotifBanner = useCallback(() => {
    setNotifBannerDismissed(true);
    localStorage.setItem("task-tracker-notif-banner-dismissed", "true");
  }, []);

  const showNotifBanner =
    selectedProjectId &&
    notifications.supported &&
    !notifications.enabled &&
    !notifBannerDismissed &&
    notifications.permission !== "denied";

  // FR-D08: Derive current page from wouter location instead of manual parseHash()
  const page = (() => {
    if (location.startsWith("/dev-server")) return "dev-server" as const;
    if (location.startsWith("/users")) return "users" as const;
    if (location.startsWith("/api-keys")) return "api-keys" as const;
    if (location.startsWith("/sessions")) return "sessions" as const;
    if (location.startsWith("/fleet")) return "fleet" as const;
    if (location.startsWith("/leaderboard")) return "leaderboard" as const;
    if (location.startsWith("/template-library")) return "template-library" as const;
    if (location.startsWith("/templates")) return "templates" as const;
    if (location.startsWith("/costs")) return "costs" as const;
    return "dashboard" as const;
  })();

  // Sync dashboard sub-routes (repo-home, project) from URL location
  useEffect(() => {
    if (page !== "dashboard") return;

    const parts = location.replace(/^\//, "").split("/").filter(Boolean).map(safeDecodeURIComponent);
    if (!parts[0]) return;
    const repoSlug = parts[0];

    if (!parts[1] || REPO_RESERVED.has(parts[1])) {
      // #/{repoSlug} or #/{repoSlug}/{repoTab}
      const repoTab = VALID_REPO_TABS.has(parts[1]) ? (parts[1] as RepoTab) : "prds";
      if (repositories) {
        const repo = repositories.find((r) => r.slug === repoSlug);
        if (repo) {
          setSelectedRepoId(repo._id);
          setSelection({ type: "repo-home", repositoryId: repo._id, repoSlug: repo.slug, repoTab });
        }
      }
    } else if (projects && repositories) {
      // #/{repoSlug}/{projectName}/{tab?}
      const projectName = parts[1];
      const parsedTab = VALID_TABS.has(parts[2]) ? (parts[2] as Tab) : "graph";
      setTab(parsedTab);
      const repo = repositories.find((r) => r.slug === repoSlug);
      const match = projects.find((p) => p.name === projectName && (!repo || p.repositoryId === repo._id));
      if (match && repo) {
        setSelectedRepoId(repo._id);
        setSelection({ type: "project", repositoryId: repo._id, repoSlug, projectId: match._id });
      }
    }
  }, [location, page, projects, repositories]);

  const handleSelectRepo = useCallback(
    (repoId: Id<"repositories">) => {
      setSelectedRepoId(repoId);
      const repo = repositories?.find((r) => r._id === repoId);
      if (repo) {
        setSelection({ type: "repo-home", repositoryId: repo._id, repoSlug: repo.slug, repoTab: "prds" });
        navigate(`/${repo.slug}/prds`);
      } else {
        setSelection(null);
      }
    },
    [repositories, navigate]
  );

  const handleSelectItem = useCallback(
    (sel: Selection) => {
      setSelection(sel);
      if (sel.type === "project") {
        setSelectedRepoId(sel.repositoryId);
        const project = projects?.find((p) => p._id === sel.projectId);
        if (project) {
          navigate(`/${encodeURIComponent(sel.repoSlug)}/${encodeURIComponent(project.name)}/${tab}`);
        }
      }
    },
    [projects, tab, navigate]
  );

  const selectedRepoSlug = selection?.type === "project" ? selection.repoSlug : null;

  const selectTab = useCallback(
    (t: Tab) => {
      setTab(t);
      if (selectedProject && selectedRepoSlug) {
        navigate(`/${encodeURIComponent(selectedRepoSlug)}/${encodeURIComponent(selectedProject.name)}/${t}`);
      }
    },
    [selectedProject, selectedRepoSlug, navigate]
  );

  return (
    <div className="min-h-screen flex flex-col">
      {updateAvailable && (
        <div className="sticky top-0 z-50 bg-amber-500/10 border-b border-amber-500/20 px-6 py-2">
          <div className="mx-auto flex items-center justify-between px-2">
            <p className="text-sm text-amber-200 flex items-center gap-2">
              Update available
              <span className="font-mono text-xs bg-amber-500/20 border border-amber-400/30 rounded px-1.5 py-0.5 text-amber-300">{serverVersion}</span>
            </p>
            <button
              onClick={refresh}
              className="text-sm font-medium text-amber-400 hover:text-amber-300 border border-amber-500/30 rounded px-3 py-1 transition-colors"
            >
              Refresh
            </button>
          </div>
        </div>
      )}
      <header className="border-b border-zinc-800 px-4 sm:px-6 py-3 sm:py-4">
        <div className="mx-auto flex items-center justify-between px-0 sm:px-2">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
            <a href="#/" className="shrink-0 hover:opacity-80 transition-opacity">
              <span className="text-lg font-semibold tracking-tight text-zinc-100 block leading-tight">Task Tracker</span>
              <span className="text-[10px] text-zinc-600 font-mono">{localVersion}</span>
            </a>
            {page === "dashboard" && (
              <ProjectSelector
                selectedRepoId={selectedRepoId}
                selection={selection}
                onSelectRepo={handleSelectRepo}
                onSelectItem={handleSelectItem}
              />
            )}
          </div>
          <div className="flex items-center gap-1 sm:gap-2 text-zinc-500">
            {notifications.supported && (
              <button
                onClick={notifications.toggle}
                className={`p-2 rounded-md transition-colors ${notifications.enabled ? "text-indigo-400 hover:text-indigo-300" : "text-zinc-600 hover:text-zinc-300"}`}
                title={notifications.enabled ? "Notifications on" : notifications.permission === "denied" ? "Notifications blocked" : "Enable notifications"}
              >
                <svg className="w-4 h-4 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  {notifications.enabled ? (
                    <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.143 17.082a24.248 24.248 0 0 0 5.714 0m-5.714 0a3 3 0 1 0 5.714 0M9.143 17.082a23.848 23.848 0 0 1-5.454-1.31A8.967 8.967 0 0 0 6 9.75V9a6 6 0 0 1 12 0v.75a8.967 8.967 0 0 0 2.312 6.022 23.848 23.848 0 0 1-5.455 1.31M3.03 3.03l17.94 17.94" />
                  )}
                </svg>
              </button>
            )}
            <a
              href="#/fleet"
              className={`p-2 rounded-md transition-colors ${page === "fleet" ? "text-indigo-400" : "text-zinc-600 hover:text-zinc-300"}`}
              title="Fleet View"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25a2.25 2.25 0 0 1-2.25-2.25V6ZM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-2.25A2.25 2.25 0 0 1 13.5 18v-2.25Z" />
              </svg>
            </a>
            <a
              href="#/leaderboard"
              className={`p-2 rounded-md transition-colors ${page === "leaderboard" ? "text-indigo-400" : "text-zinc-600 hover:text-zinc-300"}`}
              title="Leaderboard"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 18.75h-9m9 0a3 3 0 0 1 3 3h-15a3 3 0 0 1 3-3m9 0v-4.5A3.375 3.375 0 0 0 13.125 10.875h-2.25A3.375 3.375 0 0 0 7.5 14.25v4.5m6-6V6.375a3.375 3.375 0 0 0-3.375-3.375h-.75A3.375 3.375 0 0 0 6 6.375V8.25" />
              </svg>
            </a>
            <a
              href="#/costs"
              className={`p-2 rounded-md transition-colors ${page === "costs" ? "text-indigo-400" : "text-zinc-600 hover:text-zinc-300"}`}
              title="Cost Attribution"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
              </svg>
            </a>
            <a
              href="#/templates"
              className={`p-2 rounded-md transition-colors ${page === "templates" ? "text-indigo-400" : "text-zinc-600 hover:text-zinc-300"}`}
              title="Prompt Templates"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
              </svg>
            </a>
            <a
              href="#/template-library"
              className={`p-2 rounded-md transition-colors ${page === "template-library" ? "text-indigo-400" : "text-zinc-600 hover:text-zinc-300"}`}
              title="Template Library"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.36 48.36 0 0 0 12 9.75c-2.551 0-5.056.2-7.5.582V21" />
              </svg>
            </a>
            <a
              href="#/sessions"
              className={`p-2 rounded-md transition-colors ${page === "sessions" ? "text-indigo-400" : "text-zinc-600 hover:text-zinc-300"}`}
              title="Claude Sessions"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
              </svg>
            </a>
            <a
              href="#/dev-server"
              className={`p-2 rounded-md transition-colors ${page === "dev-server" ? "text-indigo-400" : "text-zinc-600 hover:text-zinc-300"}`}
              title="Dev Servers"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 14.25h13.5m-13.5 0a3 3 0 0 1-3-3m3 3a3 3 0 1 0 0 6h13.5a3 3 0 1 0 0-6m-13.5 0a3 3 0 0 1-3-3m3 3h13.5m-13.5 0a3 3 0 0 1 0-6m13.5 6a3 3 0 0 1-3-3m3 3a3 3 0 0 0 0-6m-13.5 0h13.5m0 0a3 3 0 0 0 3-3m-3 3a3 3 0 0 1 0-6m0 0a3 3 0 0 0 3-3M6.75 8.25h.008v.008H6.75V8.25Zm0 6h.008v.008H6.75v-.008ZM6.75 6h.008v.008H6.75V6Z" />
              </svg>
            </a>
            <a
              href="#/users"
              className={`p-2 rounded-md transition-colors ${page === "users" ? "text-indigo-400" : "text-zinc-600 hover:text-zinc-300"}`}
              title="Manage Users"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
              </svg>
            </a>
            <a
              href="#/api-keys"
              className={`p-2 rounded-md transition-colors ${page === "api-keys" ? "text-indigo-400" : "text-zinc-600 hover:text-zinc-300"}`}
              title="API Keys"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 0 1 3 3m3 0a6 6 0 0 1-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1 1 21.75 8.25Z" />
              </svg>
            </a>
            <span className="hidden sm:inline text-xs text-zinc-500">{user.email}</span>
            <button
              onClick={() => void signOut()}
              className="p-2 rounded-md text-zinc-600 hover:text-zinc-300 transition-colors text-xs"
            >
              Sign out
            </button>
          </div>
        </div>
        {selectedProject && page === "dashboard" && (
          <div className="mx-auto mt-2 px-0 sm:px-2">
            <span className="text-xs text-zinc-500 font-mono truncate block">
              {selectedProject.repoPath}
            </span>
          </div>
        )}
      </header>

      {showNotifBanner && (
        <div className="mx-auto w-full px-6 pt-4">
          <div className="flex items-center justify-between gap-4 bg-indigo-500/10 border border-indigo-500/20 rounded-lg px-4 py-3">
            <div className="flex items-center gap-3 min-w-0">
              <svg className="w-5 h-5 text-indigo-400 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0" />
              </svg>
              <p className="text-sm text-zinc-300">
                Get notified when tasks change status.{" "}
                <button
                  onClick={() => { notifications.toggle(); dismissNotifBanner(); }}
                  className="text-indigo-400 hover:text-indigo-300 font-medium underline underline-offset-2"
                >
                  Enable notifications
                </button>
                {" "}or use the bell icon in the top right anytime.
              </p>
            </div>
            <button
              onClick={dismissNotifBanner}
              className="text-zinc-500 hover:text-zinc-300 transition-colors text-lg leading-none shrink-0"
            >
              &times;
            </button>
          </div>
        </div>
      )}

      {/* FR-D08 / FR-023: Declarative <Switch>/<Route> replaces cascading ternary chain.
          wouter matches against hash path via useHashLocation Router. */}
      <Switch>
        {/* FR-D08 / NFR-003: CostAttribution lazy-loaded via React.lazy + Suspense */}
        <Route path="/costs">
          <main className="flex-1 w-full px-4 sm:px-6 py-4 sm:py-6">
            {selectedRepoId ? (
              <Suspense fallback={<LoadingSpinner />}>
                <CostAttributionPage repositoryId={selectedRepoId} />
              </Suspense>
            ) : (
              <div className="flex items-center justify-center py-20 text-zinc-500">
                Select a project to view cost attribution
              </div>
            )}
          </main>
        </Route>
        <Route path="/fleet">
          <main className="flex-1 w-full px-4 sm:px-6 py-4 sm:py-6">
            <FleetView />
          </main>
        </Route>
        <Route path="/leaderboard">
          <main className="flex-1 w-full px-4 sm:px-6 py-4 sm:py-6">
            <LeaderboardPage />
          </main>
        </Route>
        <Route path="/template-library">
          <main className="flex-1 w-full px-4 sm:px-6 py-4 sm:py-6">
            <TemplateLibraryPage />
          </main>
        </Route>
        <Route path="/templates">
          <main className="flex-1 w-full px-4 sm:px-6 py-4 sm:py-6">
            <div className="max-w-2xl mx-auto">
              <TemplateManager />
            </div>
          </main>
        </Route>
        <Route path="/sessions">
          <main className="flex-1 w-full px-4 sm:px-6 py-4 sm:py-6">
            <ClaudeSessionsPage />
          </main>
        </Route>
        <Route path="/users">
          <main className="flex-1 w-full px-4 sm:px-6 py-4 sm:py-6">
            <UserManagementPage />
          </main>
        </Route>
        <Route path="/api-keys">
          <main className="flex-1 w-full px-4 sm:px-6 py-4 sm:py-6">
            <ApiKeysPage />
          </main>
        </Route>
        <Route path="/dev-server">
          <main className="flex-1 w-full px-4 sm:px-6 py-4 sm:py-6">
            <DevServerPage />
          </main>
        </Route>
        {/* Dashboard routes: repo-home, project view, or empty state.
            These use selection state synced from URL via useEffect,
            so a single catch-all Route handles all dashboard sub-routes. */}
        <Route>
          {selection?.type === "repo-home" ? (
            <main className="flex-1 w-full px-4 sm:px-6 py-4 sm:py-6">
              <RepoHome
                repositoryId={selection.repositoryId}
                repoSlug={selection.repoSlug}
                tab={selection.repoTab}
              />
            </main>
          ) : selectedProjectId ? (
            <SubscriptionScope active={page === "dashboard" && !!selectedProjectId}>
              <ProjectDashboardProvider projectId={selectedProjectId}>
                <NotificationBridge onStatuses={setNotifStatuses} />
                <main className="flex-1 w-full px-4 sm:px-6 py-4 sm:py-6 space-y-4 sm:space-y-6">
                  <StatusHeader />

                  <nav className="flex flex-col sm:flex-row gap-1 border-b border-zinc-800">
                    <div className="flex gap-1 overflow-x-auto">
                      {(["graph", "list", "assets", "settings"] as Tab[]).map((t) => (
                        <button
                          key={t}
                          onClick={() => selectTab(t)}
                          className={`px-3 sm:px-4 py-2 text-sm font-medium capitalize transition-colors whitespace-nowrap ${
                            tab === t
                              ? "text-zinc-100 border-b-2 border-indigo-500"
                              : "text-zinc-500 hover:text-zinc-300"
                          }`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                    <div className="sm:ml-auto flex items-center gap-2 self-center pb-1 sm:pb-0 overflow-x-auto">
                      {unblockedTasks && unblockedTasks.length > 0 && (
                        <div className="hidden sm:flex items-center gap-1.5">
                          <span className="text-[11px] text-indigo-400 font-medium">Next:</span>
                          {unblockedTasks.slice(0, 5).map((t) => (
                            <span
                              key={t.taskId}
                              className="text-[11px] bg-indigo-500/15 text-indigo-300 px-1.5 py-0.5 rounded font-mono"
                              title={t.subject}
                            >
                              {t.taskId}
                            </span>
                          ))}
                          {unblockedTasks.length > 5 && (
                            <span className="text-[11px] text-indigo-400/60" title={unblockedTasks.slice(5).map(t => t.taskId).join(", ")}>
                              +{unblockedTasks.length - 5}
                            </span>
                          )}
                        </div>
                      )}
                      <button
                        onClick={() => setHideComplete(!hideComplete)}
                        className={`px-3 py-1.5 text-xs rounded-md border transition-colors whitespace-nowrap ${
                          hideComplete
                            ? "bg-indigo-500/15 border-indigo-500/30 text-indigo-300"
                            : "bg-zinc-900 border-zinc-700 text-zinc-400 hover:text-zinc-200"
                        }`}
                      >
                        {hideComplete ? "Showing remaining" : "Hide completed"}
                      </button>
                    </div>
                  </nav>

                  {tab === "graph" && <GraphView hideComplete={hideComplete} />}
                  {tab === "list" && <ListView hideComplete={hideComplete} />}
                  {tab === "assets" && <AssetViewer projectId={selectedProjectId} repositoryId={selectedProject?.repositoryId as Id<"repositories"> | undefined} />}
                  {tab === "settings" && <ProjectSettings projectId={selectedProjectId} />}
                </main>
              </ProjectDashboardProvider>
            </SubscriptionScope>
          ) : (
            <div className="flex-1 flex items-center justify-center text-zinc-600">
              Select a PRD execution to view task status
            </div>
          )}
        </Route>
      </Switch>
    </div>
  );
}

/**
 * Bridge component that lives inside ProjectDashboardProvider and feeds
 * task statuses up to the Dashboard for notification diff-tracking (Fix 2).
 * Replaces the direct useQuery(api.tasks.list) call that bypassed the provider.
 */
function NotificationBridge({ onStatuses }: { onStatuses: (s: { taskId: string; status: string; subject: string }[] | undefined) => void }) {
  const statuses = useRawTaskStatuses();
  useEffect(() => { onStatuses(statuses); }, [statuses, onStatuses]);
  return null;
}

class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Dashboard render error:", error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="flex flex-col items-center gap-4 p-8 text-center max-w-md">
            <div className="w-12 h-12 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center text-red-400 text-xl">
              !
            </div>
            <h2 className="text-lg font-semibold text-zinc-100">Something went wrong</h2>
            <p className="text-sm text-zinc-500 break-all">{this.state.error.message}</p>
            <button
              onClick={() => { this.setState({ error: null }); window.location.reload(); }}
              className="text-sm text-zinc-400 hover:text-zinc-200 border border-zinc-700 rounded-lg px-4 py-2 transition-colors"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export function App() {
  return (
    <ErrorBoundary>
      {/* FR-D08: wouter Router with hash location hook replaces custom parseHash() routing */}
      <Router hook={useHashLocation}>
        <ErrorToastProvider>
          <GlobalErrorCatcher />
          <AuthLoading>
            <div className="min-h-screen flex items-center justify-center text-zinc-600">Loading...</div>
          </AuthLoading>
          <Authenticated>
            <AuthenticatedApp />
          </Authenticated>
          <Unauthenticated>
            <SignInForm />
          </Unauthenticated>
        </ErrorToastProvider>
      </Router>
    </ErrorBoundary>
  );
}

function GlobalErrorCatcher() {
  const showError = useErrorToast();

  useEffect(() => {
    const handler = (event: PromiseRejectionEvent) => {
      const msg = event.reason?.message ?? String(event.reason);
      // Skip auth errors handled by main.tsx's reload logic
      if (msg.includes("OIDC token") || msg.includes("AuthError")) return;
      showError(msg);
    };
    window.addEventListener("unhandledrejection", handler);
    return () => window.removeEventListener("unhandledrejection", handler);
  }, [showError]);

  return null;
}
